import { VerifiedClient, CaseClient, ClientMatchEntry, ClientMatchMap, IDVInformation, IDVResultEvaluation } from '../../../types';
import { parseFullName, StoredIdRecord } from '../../../../../livedataFuncs';

export const isPastTargetDate = (): boolean => {
    const currentYear = new Date().getFullYear();
    return currentYear > 2024;
};

/**
 * Mirrors the `primaryList` keys in the legacy writer's `generateIdentificationNode`
 * (webapp/PurplusWeb/liveform/PurchaseFuncsNew.js). If the IDV's `clientIdFraudCheck.type`
 * isn't one of these, the legacy creates an `<Identification>` with NO `<Verification>` child;
 * the caller then dereferences `newNode.selectSingleNode("Verification")` and crashes inside
 * ajaxslt's insertBefore. Keep this set in sync if the legacy primaryList changes.
 */
const LEGACY_WRITER_SUPPORTED_ID_TYPES = new Set<string>([
    'Drivers license',
    'DlPublicServicesCard',
    'Passport',
    'ResidencePermit',
    'HealthInsuranceCard',
    'Id',
    '3', '4', '8', '9', '10', '11', '12', '13', '14_1', '14_2', '14_3', '15', '16',
]);

/**
 * True iff the IDV's document type is one the legacy writer can produce a `<Verification>` for.
 * A type absent from the mirror makes the legacy auto-import path crash, so this gates auto-import
 * on the React side. Deliberately keyed off the REPORTED type rather than the resolved grading
 * direction: the two answer different questions, and claiming a type is importable without evidence
 * from the legacy side is what this guard exists to prevent.
 */
export const isLegacyImportableIDV = (client: VerifiedClient): boolean =>
    LEGACY_WRITER_SUPPORTED_ID_TYPES.has(client.clientIdFraudCheck?.type ?? '');

/**
 * Cutoff for the #19619 IDV results reconfiguration. Verifications submitted strictly before this
 * timestamp continue to render the previous score/certainty layout; everything submitted at or after
 * this timestamp renders with the new Pass/Fail UI. Update this to the actual production go-live
 * timestamp before deployment.
 */
export const IDV_RECONFIG_GO_LIVE_TIMESTAMP = '2026-07-14T13:00:00Z';

/**
 * Beta/dev runtime override for the pre/post-reconfig gate. Flip in the browser DevTools console
 * to test the new or legacy UI regardless of the record's timestamp — no rebuild needed:
 *
 *   window.IDV_FORCE_NEW_UI = true    // force NEW post-reconfig UI for every record
 *   window.IDV_FORCE_NEW_UI = false   // force LEGACY UI for every record
 *   delete window.IDV_FORCE_NEW_UI    // back to the timestamp gate (production default)
 *
 * Refresh the "Submitted Verifications" modal (close + reopen) after flipping to re-render.
 * Do NOT set this from application code — it's a manual toggle only, so production behavior is
 * always driven by IDV_RECONFIG_GO_LIVE_TIMESTAMP.
 */
declare global {
    interface Window {
        IDV_FORCE_NEW_UI?: boolean;
    }
}

/**
 * True when the verification was submitted before the #19619 reconfiguration went live and should
 * therefore render via the legacy IDV layout (preserves the previous on-screen format for already
 * submitted IDVs per AC #1).
 *
 * Honors the {@link Window.IDV_FORCE_NEW_UI} console override for beta testing.
 */
export const isPreReconfigSubmission = (client: VerifiedClient): boolean => {
    const override = typeof window !== 'undefined' ? window.IDV_FORCE_NEW_UI : undefined;
    if (override === true) return false;   // force new UI
    if (override === false) return true;   // force legacy UI

    const raw = client.clientInformation?.Timestamp;
    if (!raw) return false;
    const submittedAt = new Date(raw.endsWith('Z') ? raw : `${raw}Z`);
    if (Number.isNaN(submittedAt.getTime())) return false;
    return submittedAt.getTime() < new Date(IDV_RECONFIG_GO_LIVE_TIMESTAMP).getTime();
};

/**
 * A client may retry their ID verification up to 3 times. 
 * The mobile app writes a per-session `retry` counter onto the fraud-check JSON: 0 on the
 * first attempt of a session, 1/2 on retries, and resets to 0 when the user scans the QR again to
 * start a fresh session. Lawyer should only see the LAST attempt of each session
 * (the success that ended the session, or the final fail) — but separate sessions are kept as
 * separate displayed records.
 *
 * Pre-reconfig records (those submitted before {@link IDV_RECONFIG_GO_LIVE_TIMESTAMP}) bypass the
 * dedup entirely so the legacy display behavior is preserved exactly — every old attempt still
 * shows up like it did before.
 */
export const dedupIDVBySession = (records: VerifiedClient[]): VerifiedClient[] => {
    const preReconfig: VerifiedClient[] = [];
    const postReconfig: VerifiedClient[] = [];
    for (const rec of records) {
        if (isPreReconfigSubmission(rec)) preReconfig.push(rec);
        else postReconfig.push(rec);
    }

    const getRetry = (rec: VerifiedClient): number | undefined => {
        try {
            const json = JSON.parse(rec.clientIdFraudCheck?.json || '{}');
            return typeof json?.retry === 'number' ? json.retry : undefined;
        } catch {
            return undefined;
        }
    };

    // Group post-reconfig records by client identity (Name + Type) and walk each group in
    // submission order. A `retry` of 0 (or undefined, for records that pre-date the field)
    // starts a new session; we keep the last attempt of each session.
    const groups = new Map<string, VerifiedClient[]>();
    for (const rec of postReconfig) {
        const name = (rec.clientInformation?.Name || '').trim();
        const type = rec.clientInformation?.Type || '';
        const groupKey = name !== ''
            ? `name:${name.toLowerCase()}|type:${type}`
            : `id:${rec.clientInformation?.id || ''}`;
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey)!.push(rec);
    }

    const sessionFinals: VerifiedClient[] = [];
    for (const group of groups.values()) {
        group.sort((a, b) => {
            const ta = new Date((a.clientInformation?.Timestamp || '') + 'Z').getTime();
            const tb = new Date((b.clientInformation?.Timestamp || '') + 'Z').getTime();
            return ta - tb;
        });
        let currentSession: VerifiedClient[] = [];
        for (const rec of group) {
            const retry = getRetry(rec);
            const startsNewSession = retry === 0 || retry === undefined;
            if (startsNewSession && currentSession.length > 0) {
                sessionFinals.push(currentSession[currentSession.length - 1]);
                currentSession = [];
            }
            currentSession.push(rec);
        }
        if (currentSession.length > 0) {
            sessionFinals.push(currentSession[currentSession.length - 1]);
        }
    }

    return [...preReconfig, ...sessionFinals];
};

const DEFAULT_FAIL_MESSAGE = 'The submitted ID failed certain checks.';
const FACE_MISMATCH_MESSAGE = 'The IDV process could not confirm that the individual on the ID is the individual who took the photograph.';
const SCREEN_FAIL_MESSAGE = 'The ID verification system detected that the ID image submitted was of an image on a screen.';
const PHOTOCOPY_FAIL_MESSAGE = 'The ID verification system detected that the ID submitted was a photocopy.';
const EXPIRED_FAIL_MESSAGE = 'The submitted ID document is expired.';
const EVALUATION_ERROR_MESSAGE = 'ID verification could not be evaluated. Please request re-verification.';
const UNSUPPORTED_ID_TYPE_FAIL_MESSAGE = 'ID type not supported.';
const UNRECOGNIZED_ID_FAIL_MESSAGE = 'The ID verification system could not identify the document type.';

/**
 * Document types rejected outright, by reported type name, before any check is evaluated.
 *
 * Add an entry here to block a document type by name
 * Right now IDs other than Passport/DL/PR/ID will be failed with the strickest rule i.e. PR card rule.
 * This list is just a way to clearly say we do not support this id, instead of this id fails some constraints
 */
const UNSUPPORTED_ID_TYPES = new Set<string>([]);

/** Lowercase and strip separators so "British Columbia", "BritishColumbia" and "TYPE_ID" compare cleanly. */
const normalizeEnumName = (value: unknown): string =>
    typeof value === 'string' ? value.toLowerCase().replace(/[^a-z]/g, '') : '';

/** Classifications that mean "BlinkID read the fields but couldn't identify the document". */
const UNCLASSIFIED_TYPE_NAMES = new Set<string>(['', 'none', 'typenone']);

/**
 * True when neither source carried a document class — the vendor read the fields but assigned the
 * document no type (its `DocumentType.NONE`).
 *
 * MicroBlink is the source of truth for what a document IS. Where it couldn't classify one we do
 * NOT substitute a judgement of our own and grade the record anyway: our per-check policy exists to
 * add scrutiny on top of the vendor's analysis, never to let a document past it. Such a record
 * fails with {@link UNRECOGNIZED_ID_FAIL_MESSAGE} so the lawyer asks for a document the reader can
 * actually identify.
 *
 * Both sources are checked because only one may carry the class: a "None" attribute must not
 * condemn a document the extraction DID classify.
 */
const isUnclassifiedDocument = (client: VerifiedClient, fraudCheckJson: any): boolean => {
    const extraction = fraudCheckJson?.fraudCheckResult?.extraction
        ?? fraudCheckJson?.fraudCheckResult?.extractionResult;
    return [client.clientIdFraudCheck?.type, extraction?.classInfo?.type]
        .map(normalizeEnumName)
        .every(name => UNCLASSIFIED_TYPE_NAMES.has(name));
};

type CheckPolicy = {
    /** Whether the ID is failed when this individual check has result Fail. */
    failOnFail: boolean;
    /** Whether the ID is failed when this individual check has result NotPerformed. */
    failOnNotPerformed: boolean;
    /** Specific screen message to surface; falls back to the default if omitted. */
    failMessage?: string;
};

type IDDocType = 'PASSPORT' | 'DL' | 'PR_CARD' | 'ID_CARD';

const STANDARD_FAIL_POLICY: CheckPolicy = { failOnFail: true, failOnNotPerformed: true };
const FAIL_ON_FAIL_ONLY_POLICY: CheckPolicy = { failOnFail: true, failOnNotPerformed: false };
const NO_EFFECT_POLICY: CheckPolicy = { failOnFail: false, failOnNotPerformed: false };

/**
 * Deviations for the driver's-licence direction. Shared with ID_CARD: the spreadsheet has no
 * identity-card tab, and a provincial photo ID card is the same PDF417-barcode North American
 * card as a licence — same front-vs-barcode MatchChecks, same BarcodeAuthenticity, same
 * VisualCheck, and (like a licence) no MRZ and no nationality field. Split the two apart if
 * product ever issues a dedicated identity-card direction.
 */
const DL_CHECK_POLICY: Record<string, CheckPolicy> = {
    'LogicCheck.DocumentNumberLogic': FAIL_ON_FAIL_ONLY_POLICY,
    'FieldFormat.DateOfBirth': FAIL_ON_FAIL_ONLY_POLICY,
    'FieldFormat.DateOfExpiry': FAIL_ON_FAIL_ONLY_POLICY,
    'FieldFormat.DateOfIssue': FAIL_ON_FAIL_ONLY_POLICY,
    'DocumentLivenessCheck.ScreenCheck': { failOnFail: true, failOnNotPerformed: true, failMessage: SCREEN_FAIL_MESSAGE },
    'DocumentLivenessCheck.PhotocopyCheck': { failOnFail: true, failOnNotPerformed: true, failMessage: PHOTOCOPY_FAIL_MESSAGE },
    'VisualCheck.SecurityFeatures': FAIL_ON_FAIL_ONLY_POLICY,
    'DocumentValidityCheck.ExpiredCheck': { failOnFail: true, failOnNotPerformed: true, failMessage: EXPIRED_FAIL_MESSAGE },
    'ProcessIndicators.HandPresence': NO_EFFECT_POLICY,
};

/**
 * ID_CARD-only loosening on top of {@link DL_CHECK_POLICY}.
 *
 * These three are the template-dependent checks: MicroBlink can only run them where it holds a
 * reference template for the exact physical layout, and its identity-card coverage is thinner than
 * its licence coverage — so a `NotPerformed` here means "no template", not "suspicious card". Under
 * the default `failOnNotPerformed: true` that alone fails the card, which is the same failure mode
 * the new AB DL layout hit (see {@link NEW_AB_DL_CHECK_OVERRIDES}). An explicit `Fail` still fails
 * the card; a skipped-for-missing-template result doesn't.
 *
 * This does NOT override the vendor: a check MicroBlink failed still fails the card. It only stops
 * a check MicroBlink never ran from counting as a failure — the same distinction the AB DL patch
 * draws.
 */
const ID_CARD_CHECK_POLICY: Record<string, CheckPolicy> = {
    ...DL_CHECK_POLICY,
    'BarcodeAuthenticity.ContentCheck': FAIL_ON_FAIL_ONLY_POLICY,
    'DocumentValidityCheck.VersionCheck': FAIL_ON_FAIL_ONLY_POLICY,
    'VisualCheck.PhotoForgeryCheck': FAIL_ON_FAIL_ONLY_POLICY,
};

/**
 * Per-check policy lookup keyed by ID document type, then by "ParentCheckName.ChildCheckName".
 * Sourced from the IDV JSON Analysis Direction spreadsheet (one tab per ID type).
 *
 * Only deviations from {@link STANDARD_FAIL_POLICY} need entries here — any check not listed
 * defaults to "Fail ID on both Fail and NotPerformed" with the default screen message.
 *
 * Unrecognized ID types fall back to PR_CARD direction.
 */
const CHECK_POLICY_BY_TYPE: Record<IDDocType, Record<string, CheckPolicy>> = {
    PASSPORT: {
        'FieldFormat.DateOfBirth': FAIL_ON_FAIL_ONLY_POLICY,
        'FieldFormat.DateOfExpiry': FAIL_ON_FAIL_ONLY_POLICY,
        'FieldFormat.DateOfIssue': FAIL_ON_FAIL_ONLY_POLICY,
        'DocumentLivenessCheck.ScreenCheck': { failOnFail: true, failOnNotPerformed: false, failMessage: SCREEN_FAIL_MESSAGE },
        'DocumentLivenessCheck.PhotocopyCheck': { failOnFail: true, failOnNotPerformed: false, failMessage: PHOTOCOPY_FAIL_MESSAGE },
        'DocumentValidityCheck.ExpiredCheck': { failOnFail: true, failOnNotPerformed: true, failMessage: EXPIRED_FAIL_MESSAGE },
        'ProcessIndicators.HandPresence': NO_EFFECT_POLICY,
    },
    DL: DL_CHECK_POLICY,
    ID_CARD: ID_CARD_CHECK_POLICY,
    PR_CARD: {
        'DocumentLivenessCheck.ScreenCheck': { failOnFail: true, failOnNotPerformed: true, failMessage: SCREEN_FAIL_MESSAGE },
        'DocumentLivenessCheck.PhotocopyCheck': { failOnFail: true, failOnNotPerformed: true, failMessage: PHOTOCOPY_FAIL_MESSAGE },
        'DocumentValidityCheck.ExpiredCheck': { failOnFail: true, failOnNotPerformed: true, failMessage: EXPIRED_FAIL_MESSAGE },
        'ProcessIndicators.HandPresence': NO_EFFECT_POLICY,
    },
};

/**
 * Canonicalize a reported document type onto one of the four grading directions. Each of the four
 * — passport, driver's licence, PR card and photo ID card — is matched by name.
 *
 * The final `PR_CARD` is the gate for everything NOT in those four families (ConsularId,
 * MilitaryId, VoterId, GreenCard, SocialSecurityCard, …): that sheet requires an MRZ and a
 * nationality field, so an unrecognized document cannot pass it. That's deliberate — a document
 * type nobody has written a direction for should not be quietly analyzed as though one existed.
 */
const toIDDocType = (raw: string | undefined): IDDocType => {
    if (!raw) return 'PR_CARD';
    const normalized = raw.toLowerCase().trim();
    // TYPE_* enum values from the vendor's classInfo.type
    if (normalized === 'type_passport') return 'PASSPORT';
    if (normalized === 'type_dl') return 'DL';
    if (normalized === 'type_residence_permit') return 'PR_CARD';
    // Free-form strings written into the Entity's Type attribute by the mobile app
    // (e.g. "Drivers license", "Driver's License", "Passport", "PR Card").
    if (normalized.includes('passport')) return 'PASSPORT';
    // BlinkID documentType enum names for driver's-licence variants that don't contain "driver"
    // (e.g. the BC combined Driver's Licence + Services Card) — without these they fall through
    // to the PR_CARD policy and auto-fail on MRZ/Nationality checks a licence can never satisfy.
    if (normalized.includes('driver') || normalized === 'dlpublicservicescard' || normalized === 'professionaldl') return 'DL';
    if (normalized.includes('pr card') || normalized.includes('permanent resident') || normalized.includes('residence')) return 'PR_CARD';
    // Photo ID cards — the fourth recognized family. Only classes the vendor actually
    // assigned appear here; an unclassified document is rejected up front by
    // {@link isUnclassifiedDocument} and never reaches this direction. "identity card" / "id card"
    // cover the readable labels a stored record can carry, so a stored ID and a live scan
    // canonicalize the same way.
    if (normalized === 'id' || normalized === 'type_id' || normalized === 'type_id_card'
        || normalized === 'publicservicescard'
        || normalized.includes('identity card') || normalized.includes('id card')) return 'ID_CARD';
    return 'PR_CARD';
};

const resolveIDDocType = (client: VerifiedClient, fraudCheckJson: any): IDDocType => {
    const fromAttr = client.clientIdFraudCheck?.type;
    // A "None" attribute means the mobile app had no classification to write, so it must not
    // outvote the extraction's — a licence the extraction DID classify has to be graded as one.
    if (fromAttr && !UNCLASSIFIED_TYPE_NAMES.has(normalizeEnumName(fromAttr))) {
        return toIDDocType(fromAttr);
    }
    const extraction = fraudCheckJson?.fraudCheckResult?.extraction
        ?? fraudCheckJson?.fraudCheckResult?.extractionResult;
    return toIDDocType(extraction?.classInfo?.type);
};

/**
 * Fallback when a check is not on the spreadsheet for this ID type. Per product, an unknown check
 * fails the ID if it explicitly returns `Fail` (use the default message); a Pass or NotPerformed
 * on an unknown check is ignored. In-spreadsheet checks always go through their explicit policy.
 */
const UNKNOWN_CHECK_FALLBACK_POLICY: CheckPolicy = FAIL_ON_FAIL_ONLY_POLICY;

/**
 * The driver's-licence sheet, shared with ID_CARD (see {@link DL_CHECK_POLICY}). Note what is
 * absent and therefore ignored-unless-Fail for both: `MRZCheck.*` and `FieldFormat.Nationality`,
 * neither of which exists on a North American barcode card.
 */
const DL_KNOWN_CHECKS = new Set([
    'MatchCheck.FirstName', 'MatchCheck.LastName', 'MatchCheck.Address',
    'MatchCheck.DateOfBirth', 'MatchCheck.DateOfIssue', 'MatchCheck.DateOfExpiry',
    'MatchCheck.DocumentNumber',
    'LogicCheck.DateOfBirthBeforeDateOfIssueCheck', 'LogicCheck.DateOfBirthBeforeDateOfExpiryCheck',
    'LogicCheck.DateOfIssueBeforeDateOfExpiryCheck', 'LogicCheck.DateOfBirthInPastCheck',
    'LogicCheck.DateOfIssueInPastCheck', 'LogicCheck.DocumentNumberLogic',
    'FieldFormat.DateOfBirth', 'FieldFormat.DateOfExpiry', 'FieldFormat.DateOfIssue',
    'FieldFormat.DocumentNumber', 'FieldFormat.Sex',
    'BarcodeAuthenticity.ContentCheck', 'BarcodeAuthenticity.ReadCheck',
    'SuspiciousDataCheck.SuspiciousNumberCheck', 'SuspiciousDataCheck.SampleStringCheck',
    'SuspiciousDataCheck.DataIntegrityCheck',
    'DocumentLivenessCheck.ScreenCheck', 'DocumentLivenessCheck.PhotocopyCheck',
    'VisualCheck.PhotoForgeryCheck', 'VisualCheck.SecurityFeatures',
    'DocumentValidityCheck.VersionCheck', 'DocumentValidityCheck.ExpiredCheck',
    'ProcessIndicators.Clarity', 'ProcessIndicators.HandPresence', 'ProcessIndicators.Cropped',
]);

/**
 * 
 * Every check listed on each sheet of the IDV JSON Analysis Direction spreadsheet. Used to
 * distinguish "in the spreadsheet, no special policy → standard Fail/Fail behavior" from
 * "not in the spreadsheet → ignore entirely." {@link CHECK_POLICY_BY_TYPE} only carries the
 * deviations from {@link STANDARD_FAIL_POLICY}; this set carries the full list.
 */
const KNOWN_CHECKS_BY_TYPE: Record<IDDocType, Set<string>> = {
    PASSPORT: new Set([
        'MatchCheck.FirstName', 'MatchCheck.LastName', 'MatchCheck.DateOfBirth', 'MatchCheck.DateOfExpiry',
        'LogicCheck.DateOfBirthBeforeDateOfIssueCheck', 'LogicCheck.DateOfBirthBeforeDateOfExpiryCheck',
        'LogicCheck.DateOfIssueBeforeDateOfExpiryCheck', 'LogicCheck.DateOfBirthInPastCheck',
        'LogicCheck.DateOfIssueInPastCheck',
        'FieldFormat.DateOfBirth', 'FieldFormat.DateOfExpiry', 'FieldFormat.DateOfIssue',
        'FieldFormat.DocumentNumber', 'FieldFormat.Sex', 'FieldFormat.Nationality',
        'SuspiciousDataCheck.SuspiciousNumberCheck', 'SuspiciousDataCheck.SampleStringCheck',
        'SuspiciousDataCheck.DataIntegrityCheck',
        'MRZCheck.Parsed', 'MRZCheck.CheckDigits',
        'DocumentLivenessCheck.ScreenCheck', 'DocumentLivenessCheck.PhotocopyCheck',
        'VisualCheck.PhotoForgeryCheck',
        'DocumentValidityCheck.VersionCheck', 'DocumentValidityCheck.ExpiredCheck',
        'ProcessIndicators.Clarity', 'ProcessIndicators.HandPresence', 'ProcessIndicators.Cropped',
    ]),
    DL: DL_KNOWN_CHECKS,
    // Same barcode-card check list as a licence — see DL_CHECK_POLICY.
    ID_CARD: DL_KNOWN_CHECKS,
    PR_CARD: new Set([
        'MatchCheck.DateOfBirth', 'MatchCheck.DateOfExpiry',
        'MatchCheck.DocumentNumber', 'MatchCheck.DocumentAdditionalNumber',
        'LogicCheck.DateOfBirthBeforeDateOfExpiryCheck', 'LogicCheck.DateOfBirthInPastCheck',
        'FieldFormat.DateOfBirth', 'FieldFormat.DateOfExpiry',
        'FieldFormat.DocumentNumber', 'FieldFormat.DocumentAdditionalNumber',
        'FieldFormat.Sex', 'FieldFormat.Nationality',
        'SuspiciousDataCheck.SuspiciousNumberCheck', 'SuspiciousDataCheck.SampleStringCheck',
        'SuspiciousDataCheck.DataIntegrityCheck',
        'MRZCheck.Parsed', 'MRZCheck.CheckDigits',
        'DocumentLivenessCheck.ScreenCheck', 'DocumentLivenessCheck.PhotocopyCheck',
        'VisualCheck.PhotoForgeryCheck',
        'DocumentValidityCheck.VersionCheck', 'DocumentValidityCheck.ExpiredCheck',
        'ProcessIndicators.Clarity', 'ProcessIndicators.HandPresence', 'ProcessIndicators.Cropped',
    ]),
};

const getCheckPolicy = (idDocType: IDDocType, parentName: string, childName: string): CheckPolicy => {
    const key = `${parentName}.${childName}`;
    const deviation = CHECK_POLICY_BY_TYPE[idDocType][key];
    if (deviation) return deviation;
    if (KNOWN_CHECKS_BY_TYPE[idDocType].has(key)) return STANDARD_FAIL_POLICY;
    return UNKNOWN_CHECK_FALLBACK_POLICY;
};

/**
 * Walks the fraudCheckResult tree to every leaf check (a node with no further `checks` array)
 * and yields each one with its immediate named parent. Vendor responses sometimes wrap groups
 * in extra layers (e.g. an `ExtractedDataCheck` parent above the spreadsheet's `MatchCheck` /
 * `MRZCheck` / etc.), so a flat 2-level walk would miss real leaves and treat wrappers as fake
 * leaves. The recursive walk drills past wrappers and lands on actual leaves regardless of depth.
 */
const visitLeafChecks = (
    node: any,
    parentName: string,
    visit: (parentName: string, leafName: string, result: string) => void,
): void => {
    if (!node) return;
    const children: any[] = Array.isArray(node.checks) ? node.checks : [];
    if (children.length === 0) {
        const leafName: string = node.name ?? '';
        const result: string = node.result ?? '';
        if (leafName) visit(parentName, leafName, result);
        return;
    }
    const nextParent: string = node.name || parentName;
    for (const child of children) visitLeafChecks(child, nextParent, visit);
};

/**
 * TEMPORARY PATCH — BUG 21499: new Alberta DL layout (issued 2026-07-02+).
 * MicroBlink doesn't yet have reference templates for the new physical layout, so these three
 * checks come back `NotPerformed` on every scan (regardless of image quality). Per the spec
 * the default is `failOnNotPerformed: true`, which would flip every new AB DL to Fail even
 * though MicroBlink returned no actual `Fail` results. Loosen those three specifically to
 * `FAIL_ON_FAIL_ONLY` — an explicit `Fail` still fails the record, but a `NotPerformed`
 * (skipped-for-missing-template) doesn't. Applies ONLY to records matching the new AB DL
 * signals; every other DL still uses the strict spec policy.
 * Remove once MicroBlink ships recognizer support for the new layout.
 */
const NEW_AB_DL_CHECK_OVERRIDES: Record<string, CheckPolicy> = {
    'BarcodeAuthenticity.ContentCheck': FAIL_ON_FAIL_ONLY_POLICY,
    'VisualCheck.PhotoForgeryCheck': FAIL_ON_FAIL_ONLY_POLICY,
    'DocumentValidityCheck.VersionCheck': FAIL_ON_FAIL_ONLY_POLICY,
};

const isNewAlbertaDLFraudCheck = (fraudCheckJson: any): boolean => {
    const classInfo = fraudCheckJson?.classInfo;
    const doi = fraudCheckJson?.dateOfIssue;
    const vehicleClassRaw = fraudCheckJson?.driverLicenseDetailedInfo?.vehicleClass;
    const vehicleClass = (typeof vehicleClassRaw === 'string'
        ? vehicleClassRaw
        : vehicleClassRaw?.latin ?? '').trim();
    const NEW_AB_DL_ISSUE_CUTOFF_UTC = Date.UTC(2026, 6, 2); // JS months are 0-indexed
    const issueUTC = doi?.successfullyParsed &&
        typeof doi.year === 'number' &&
        typeof doi.month === 'number' &&
        typeof doi.day === 'number'
        ? Date.UTC(doi.year, doi.month - 1, doi.day)
        : NaN;
    return classInfo?.country === 14
        && classInfo?.region === 3
        && vehicleClass === '5'
        && Number.isFinite(issueUTC)
        && issueUTC >= NEW_AB_DL_ISSUE_CUTOFF_UTC;
};

/**
 * Collect every leaf check that fails the ID per the per-ID-type policy.
 *
 * Behavior:
 *   - On the spreadsheet → strictly follow the policy (e.g. `HandPresence` Fail = No Affect,
 *     ScreenCheck Fail uses its specific message, etc.).
 *   - Not on the spreadsheet → fail only if the result is `Fail` (with the default message);
 *     Pass / NotPerformed for unknown checks are ignored.
 *
 * Both rules are encoded inside the `CheckPolicy` returned by {@link getCheckPolicy} — the
 * per-type tables for known checks, {@link UNKNOWN_CHECK_FALLBACK_POLICY} for the rest.
 *
 * `perCheckOverrides`, when supplied, takes precedence over the built-in policy for any key
 * it defines. Used to loosen specific checks for known document variants where the vendor
 * consistently returns `NotPerformed` for structural reasons (e.g. new AB DL layout, BUG 21499).
 */
const collectFailingChecks = (
    fraudCheckResult: any,
    idDocType: IDDocType,
    perCheckOverrides?: Record<string, CheckPolicy>,
): { policy: CheckPolicy }[] => {
    const failures: { policy: CheckPolicy }[] = [];
    if (!fraudCheckResult || !Array.isArray(fraudCheckResult.checks)) return failures;

    for (const top of fraudCheckResult.checks) {
        visitLeafChecks(top, '', (parentName, leafName, result) => {
            const key = `${parentName}.${leafName}`;
            const policy = perCheckOverrides?.[key] ?? getCheckPolicy(idDocType, parentName, leafName);
            if (result === 'Fail' && policy.failOnFail) {
                failures.push({ policy });
            } else if (result !== 'Fail' && result !== 'Pass' && policy.failOnNotPerformed) {
                // Anything that isn't an explicit Pass/Fail is treated as NotPerformed.
                failures.push({ policy });
            }
        });
    }
    return failures;
};

/**
 * Evaluate the on-screen Pass/Fail outcome for an IDV record.
 * Walks the fraudCheckResult JSON and applies the per-check policy from the spec spreadsheet
 * (per ID-document type — Passport, Driver's License, and PR Card each have their own rules).
 * Face verification is reported separately because its failure stacks on top of any ID-fail message.
 */
export const evaluateIDVResult = (client: VerifiedClient): IDVResultEvaluation => {
    let fraudCheckJson: any;
    try {
        fraudCheckJson = JSON.parse(client.clientIdFraudCheck?.json || '{}');
    } catch {
        fraudCheckJson = {};
    }

    const faceVerificationFailed = client.skipFaceVerification === false && client.FaceVerificationPassed === false;
    const faceMismatchMessage = faceVerificationFailed ? FACE_MISMATCH_MESSAGE : undefined;

    const idDocType = resolveIDDocType(client, fraudCheckJson);

    // MicroBlink couldn't identify the document, so there is nothing for our checks to enhance —
    // fail with the real reason rather than grading it against a direction we picked ourselves.
    if (isUnclassifiedDocument(client, fraudCheckJson)) {
        return {
            outcome: 'Fail',
            failMessage: UNRECOGNIZED_ID_FAIL_MESSAGE,
            faceVerificationFailed,
            faceMismatchMessage,
        };
    }

    // Fail immediately for document types product has flagged as unsupported, before any check is
    // evaluated — the lawyer should request a different document rather than read a check failure
    // that looks like the card might be fraudulent. {@link UNSUPPORTED_ID_TYPES} is empty as of now
    if (UNSUPPORTED_ID_TYPES.has(client.clientIdFraudCheck?.type ?? '')) {
        return {
            outcome: 'Fail',
            failMessage: UNSUPPORTED_ID_TYPE_FAIL_MESSAGE,
            faceVerificationFailed,
            faceMismatchMessage,
        };
    }

    // Fail closed when the vendor response is missing or malformed
    const fraudCheckResult = fraudCheckJson?.fraudCheckResult;
    if (!fraudCheckResult || !Array.isArray(fraudCheckResult.checks)) {
        return {
            outcome: 'Fail',
            failMessage: EVALUATION_ERROR_MESSAGE,
            faceVerificationFailed,
            faceMismatchMessage,
        };
    }

    // BUG 21499: loosen the three template-dependent checks (they always come back as
    // NotPerformed for the new AB DL layout — see NEW_AB_DL_CHECK_OVERRIDES comment).
    const perCheckOverrides = isNewAlbertaDLFraudCheck(fraudCheckJson) ? NEW_AB_DL_CHECK_OVERRIDES : undefined;
    const failures = collectFailingChecks(fraudCheckResult, idDocType, perCheckOverrides);

    let outcome: 'Pass' | 'Fail' = 'Pass';
    let failMessage: string | undefined;
    if (failures.length === 1) {
        outcome = 'Fail';
        failMessage = failures[0].policy.failMessage ?? DEFAULT_FAIL_MESSAGE;
    } else if (failures.length > 1) {
        outcome = 'Fail';
        failMessage = DEFAULT_FAIL_MESSAGE;
    }

    // the overall result also fails when face verification fails — the main Pass/Fail
    // relies on the face check passing. The specific face-mismatch reason is surfaced separately
    // via `faceMismatchMessage`, so we only need to flip the outcome here (no document failMessage
    // is set when the document checks themselves passed).
    if (faceVerificationFailed) {
        outcome = 'Fail';
    }

    return {
        outcome,
        failMessage,
        faceVerificationFailed,
        faceMismatchMessage,
    };
};

const pickLocalizedString = (field: any): string | undefined => {
    if (!field) return undefined;
    if (typeof field === 'string') return field.trim() || undefined;
    const candidate = field.latin || field.cyrillic || field.arabic;
    return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : undefined;
};

const formatDateField = (field: any): string | undefined => {
    if (!field || typeof field !== 'object') return undefined;
    const { year, month, day, successfullyParsed } = field;
    if (successfullyParsed === false) return undefined;
    if (!year || !month || !day) return undefined;
    return `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
};

const ID_TYPE_LABELS: Record<string, string> = {
    TYPE_DL: "Driver's License",
    TYPE_PASSPORT: 'Passport',
    TYPE_RESIDENCE_PERMIT: 'Residence Permit',
    TYPE_ID: 'Identity Card',
    TYPE_ID_CARD: 'Identity Card',
    Id: 'Identity Card',
};

/**
 * Label for the ID Type row: the vendor's own classification, verbatim, with {@link ID_TYPE_LABELS}
 * only translating its enum names into readable ones.
 *
 * Deliberately NOT derived from {@link resolveIDDocType}. The ID Information panel reports what
 * MicroBlink extracted, so an unclassified document must render as the "None" the vendor actually
 * returned. We may still choose to grade such a card against the ID_CARD direction (see
 * {@link SUPPORTED_ID_CARD_PROVINCES}) — that's our risk decision — but this row must not restate
 * it as though the vendor had identified the document.
 */
const formatIdType = (rawType: string | undefined): string | undefined => {
    if (!rawType) return undefined;
    return ID_TYPE_LABELS[rawType] ?? rawType;
};

/**
 * The vendor's classification for the ID Type row, taken from whichever of its two engines actually
 * assigned one — same precedence as {@link resolveIDDocType}, so the panel and the Pass/Fail verdict
 * can no longer disagree.
 *
 * Both sources are MicroBlink's: `clientIdFraudCheck.type` comes from the pinned browser SDK, while
 * `extraction.classInfo.type` comes from the docver service, which runs a much newer recognizer. The
 * SDK reports "None" for documents docver classifies fine — the BC Identity Card being the case that
 * prompted this, where the SDK sent `documentType: 0` and docver sent `"Id"` in the same
 * payload. When neither engine assigned a class, the vendor's own "None" is still shown verbatim; we
 * never substitute a guess of our own.
 */
const pickReportedIdType = (client: VerifiedClient, classInfo: any): string | undefined => {
    const fromAttr = client.clientIdFraudCheck?.type;
    if (fromAttr && !UNCLASSIFIED_TYPE_NAMES.has(normalizeEnumName(fromAttr))) return fromAttr;
    return classInfo?.type ?? fromAttr;
};

const formatSex = (rawSex: any): string | undefined => {
    const value = pickLocalizedString(rawSex);
    if (!value) return undefined;
    const normalized = value.toUpperCase();
    if (normalized === 'M' || normalized === 'MALE') return 'M';
    if (normalized === 'F' || normalized === 'FEMALE') return 'F';
    return value;
};

/**
 * Pull the typed ID Information fields out of the fraud-check JSON for the new info panel.
 * Returns undefined for any field that wasn't extracted so callers can skip its row entirely.
 */
export const extractIDVInformation = (client: VerifiedClient): IDVInformation => {
    let fraudCheckJson: any;
    try {
        fraudCheckJson = JSON.parse(client.clientIdFraudCheck?.json || '{}');
    } catch {
        fraudCheckJson = {};
    }

    const extraction = fraudCheckJson.fraudCheckResult?.extraction
        ?? fraudCheckJson.fraudCheckResult?.extractionResult
        ?? {};
    const classInfo = extraction?.classInfo ?? {};

    return {
        givenName: pickLocalizedString(fraudCheckJson.firstName),
        surname: pickLocalizedString(fraudCheckJson.lastName),
        sex: formatSex(fraudCheckJson.sex),
        idType: formatIdType(pickReportedIdType(client, classInfo)),
        issuingJurisdiction: pickLocalizedString(classInfo.region) ?? pickLocalizedString(classInfo.country),
        documentNumber: pickLocalizedString(fraudCheckJson.documentNumber),
        dateOfExpiry: formatDateField(fraudCheckJson.dateOfExpiry),
        dateOfBirth: formatDateField(fraudCheckJson.dateOfBirth),
    };
};

const normalizeIdNumber = (value: string | undefined): string =>
    (value || '').replace(/\s+/g, '').toUpperCase();

// Normalize a date to YYYYMMDD for comparison. The React side emits "YYYY/MM/DD" (zero-padded),
// while the legacy writer emits "YYYY-MM-DD" with the DAY NOT zero-padded (e.g. "2027-03-5"), so a
// naive digit-strip would disagree for days 1-9. Parse the Y/M/D parts and re-pad both sides.
const normalizeIdDate = (value: string | undefined): string => {
    const m = (value || '').match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
    return m ? m[1] + m[2].padStart(2, '0') + m[3].padStart(2, '0') : (value || '').replace(/[^0-9]/g, '');
};

// Resolve a document number the way the legacy getNumberVal does: a plain string, or an object with
// the latin || arabic || cyrillic precedence (NOT pickLocalizedString's latin || cyrillic || arabic),
// so multi-script numbers resolve to the same string the writer stored in Verification@Number.
const pickWriterNumber = (field: any): string | undefined => {
    if (!field) return undefined;
    if (typeof field === 'string') return field.trim() || undefined;
    const candidate = field.latin || field.arabic || field.cyrillic;
    return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : undefined;
};

/**
 * True when this IDV scan is already stored on the matched client, so re-importing would append a
 * duplicate <Verification> (the legacy importIdentification has no dedupe of its own).
 *
 * Compare key = document number + canonical document type + expiry date:
 *  - The document NUMBER is the primary key. We mirror the legacy writer's Verification@Number
 *    selection exactly: for residence permits it stores documentAdditionalNumber (when present),
 *    otherwise documentNumber — so for PR cards we match against either candidate. The number is
 *    resolved with the writer's latin||arabic||cyrillic precedence so multi-script numbers align.
 *  - Type is compared canonically (toIDDocType) rather than by display label, and Country/Jurisdiction
 *    are intentionally NOT compared: those labels are produced by different code on each side, so
 *    matching on them would risk MISSING a real duplicate.
 *  - A renewal reuses the number with a NEW expiry, so a differing expiry is deliberately treated as
 *    a different ID (not a duplicate).
 *  - When no number can be extracted we return false (don't suppress) so a genuinely new ID is
 *    never silently dropped.
 */
export const idvAlreadyImported = (
    client: VerifiedClient,
    existingIds: StoredIdRecord[],
): boolean => {
    let fraudCheckJson: any;
    try { fraudCheckJson = JSON.parse(client.clientIdFraudCheck?.json || '{}'); } catch { fraudCheckJson = {}; }
    const docType = resolveIDDocType(client, fraudCheckJson);

    // Candidate numbers, mirroring the legacy writer: PR cards may store documentAdditionalNumber.
    const candidates = [pickWriterNumber(fraudCheckJson.documentNumber)];
    if (docType === 'PR_CARD') {
        candidates.push(pickWriterNumber(fraudCheckJson.documentAdditionalNumber));
    }
    const numbers = candidates.map(normalizeIdNumber).filter(Boolean);
    if (numbers.length === 0) return false;

    const expiry = normalizeIdDate(formatDateField(fraudCheckJson.dateOfExpiry));

    return (existingIds || []).some(rec => {
        const recNumber = normalizeIdNumber(rec.number);
        return !!recNumber
            && numbers.includes(recNumber)
            && toIDDocType(rec.type) === docType
            && normalizeIdDate(rec.expiry) === expiry;
    });
};

export const getAutoFailReasons = (client: VerifiedClient): string[] => {
    const fraudCheck = client.clientIdFraudCheck;
    const failureReasons = [];
    
    if (fraudCheck?.expiredCheck === 'FAIL') {
        failureReasons.push('Document Expired.');
    }
    if (fraudCheck?.screenCheck === 'FAIL') {
        failureReasons.push('Image is of an ID on a screen.');
    }
    if (fraudCheck?.photocopyCheck === 'FAIL') {
        failureReasons.push('Image is of a photocopied ID.');
    }
    
    return failureReasons;
};

export const isScoreAlreadyZero = (client: VerifiedClient): boolean => {
    const score = client.clientIdFraudCheck?.score;
    return score === '0%' || score === '0';
};

/**
 * Generate a match key from a full name string.
 * Uses the existing parseFullName from livedataFuncs which handles both
 * "LastName, FirstName MiddleName" and "FirstName MiddleName LastName" formats.
 * Key = full last name (lowercased) + "_" + first 2 letters of first name (lowercased).
 */
export const generateMatchKey = (fullName: string): string | null => {
    const [firstName, , lastName] = parseFullName(fullName);
    if (!lastName || firstName.length < 2) return null;
    return `${lastName.toLowerCase()}_${firstName.substring(0, 2).toLowerCase()}`;
};

/**
 * Build a map from case clients for fast IDV-to-client matching.
 * Key: "lastname_fi" (full last name + first 2 chars of first name, all lowercase)
 * Value: { client, isDuplicate }
 * If multiple clients produce the same key, isDuplicate is set to true.
 */
export const buildClientMatchMap = (caseClients: CaseClient[]): ClientMatchMap => {
    const map: ClientMatchMap = new Map();
    for (const client of caseClients) {
        const key = generateMatchKey(client.name);
        if (key === null) continue;
        const existing = map.get(key);
        if (existing) {
            map.set(key, { client: existing.client, isDuplicate: true });
        } else {
            map.set(key, { client, isDuplicate: false });
        }
    }
    return map;
};

/**
 * Look up a matched client for a given IDV record name.
 * Returns the ClientMatchEntry if found (check isDuplicate before using), or undefined.
 */
export const findClientMatch = (idvName: string, matchMap: ClientMatchMap): ClientMatchEntry | undefined => {
    const key = generateMatchKey(idvName);
    if (key === null) return undefined;
    return matchMap.get(key);
};
